import { useCallback, useEffect, useRef, useState } from "react";
import { Lock, LockOpen, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import useApiMethods from "../../lib/useApiMethods";
import { CUSTOM_CONFIGURATION } from "../../constants/api";
import { persistCustomConfig } from "../../utils/customConfigSession";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";

const readLockFromSession = () => {
    try {
        const seed = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
        return {
            locked: !!seed.lock_employee_salary_payslip,
            configId: seed.id ?? null,
        };
    } catch {
        return { locked: false, configId: null };
    }
};

/**
 * FR-042 — banner on Run Payroll screens when employee My Profile salary/payslip lock is ON.
 * Payroll editors can toggle lock without leaving the payroll flow.
 */
const PayrollProfileLockBanner = ({ className }) => {
    const { fetch, update } = useApiMethods();
    const { edit: payrollEdit } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const initial = readLockFromSession();
    const [saving, setSaving] = useState(false);
    const [locked, setLocked] = useState(initial.locked);
    const [configId, setConfigId] = useState(initial.configId);

    const loadLockStatus = useCallback(async () => {
        try {
            const res = await fetchRef.current(CUSTOM_CONFIGURATION);
            if (res?.status !== "success" || !res.data?.[0]) return;

            const cfg = res.data[0];
            const nextLocked = !!cfg.lock_employee_salary_payslip;
            setLocked(nextLocked);
            if (cfg.id) setConfigId(cfg.id);
            persistCustomConfig({
                ...cfg,
                lock_employee_salary_payslip: nextLocked,
            });
        } catch (err) {
            ToastMsg("error", err?.message || "Failed to load lock status");
        }
    }, []);

    // Keep in sync when Settings or another screen updates customConfig.
    useEffect(() => {
        const syncFromSession = () => {
            const next = readLockFromSession();
            setLocked(next.locked);
            if (next.configId) setConfigId(next.configId);
        };

        window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, syncFromSession);
        return () => window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, syncFromSession);
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            await loadLockStatus();
            if (cancelled) return;
        })();

        return () => {
            cancelled = true;
        };
    }, [loadLockStatus]);

    const toggleLock = async () => {
        if (!payrollEdit) {
            ToastMsg("error", "You don't have permission to change the lock");
            return;
        }
        const next = !locked;
        setSaving(true);
        try {
            const payload = {
                lock_employee_salary_payslip: next,
            };
            if (configId) {
                payload.id = configId;
            }

            const res = await update(CUSTOM_CONFIGURATION, payload);
            if (res?.status === "success") {
                const savedLocked = res?.data?.lock_employee_salary_payslip ?? next;
                setLocked(!!savedLocked);
                persistCustomConfig({
                    ...(configId ? { id: configId } : {}),
                    lock_employee_salary_payslip: !!savedLocked,
                });
                await loadLockStatus();
                ToastMsg(
                    "success",
                    savedLocked
                        ? "Employee Salary & Payslips locked on My Profile"
                        : "Employee Salary & Payslips unlocked on My Profile",
                );
            } else {
                ToastMsg("error", res?.message || "Failed to update lock");
            }
        } catch (err) {
            ToastMsg("error", err?.message || "Failed to update lock");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className={cn(
                "rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3",
                locked
                    ? "border-amber-300/80 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60"
                    : "border-emerald-200/80 bg-emerald-50/80 dark:bg-emerald-950/20 dark:border-emerald-900/50",
                className,
            )}
        >
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <span
                    className={cn(
                        "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                        locked
                            ? "bg-amber-200/80 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
                            : "bg-emerald-200/80 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
                    )}
                >
                    {locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                </span>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {locked ? "Lock Process is ON" : "Lock Process is OFF"}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                        {locked
                            ? "Employees cannot view Salary or Payslips on My Profile until you release the lock."
                            : "Employees can view Salary and Payslips on My Profile."}
                        {" "}
                        <Link to="/settings/configuration" className="text-sidebar-primary hover:underline font-medium">
                            Settings
                        </Link>
                    </p>
                    {payrollEdit && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Payroll admins with edit access can still view payslips while the lock is on.
                        </p>
                    )}
                </div>
            </div>
            {payrollEdit && (
                <button
                    type="button"
                    onClick={toggleLock}
                    disabled={saving}
                    className={cn(
                        "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shrink-0 transition-colors disabled:opacity-60",
                        locked
                            ? "bg-white dark:bg-gray-800 border border-amber-300 text-amber-900 dark:text-amber-100 hover:bg-amber-100/80"
                            : "bg-sidebar-primary text-white hover:bg-sidebar-primary/90",
                    )}
                >
                    {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : locked ? (
                        <LockOpen className="h-4 w-4" />
                    ) : (
                        <Lock className="h-4 w-4" />
                    )}
                    {locked ? "Unlock Process" : "Lock Process"}
                </button>
            )}
        </div>
    );
};

export default PayrollProfileLockBanner;
